import React, {useEffect, useRef, useState} from 'react';
import SwiperCore, {A11y, Autoplay, Navigation, Pagination, Scrollbar} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import CustomerCommentService from '../../services/customer-comment.service';
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Autoplay]);
import 'swiper/swiper-bundle.min.css';
import styles from './CustomerComments.module.css';
import {getLocaleContent} from '@helper/locale-content.js';
import {useRouter} from 'next/router';
import MediaPopup from './MediaPopup';

export default function CustomerComments() {
    const {locale} = useRouter();
    const [customerComments, setCustomerComments] = useState([]);
    const [currentSlidesPerView, setCurrentSlidesPerView] = useState(1);
    // REMOVE manual activeIndex tracking (Swiper gives us isActive)
    // const [activeIndex, setActiveIndex] = useState(0);

    const [playingIndex, setPlayingIndex] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const videoRefs = useRef([]);
    const [isMediaPopupOpen, setIsMediaPopupOpen] = useState(false);
    const [customerComment, setCustomerComment] = useState({media_url: '', media_type: 'image'});
    const swiperRef = useRef(null);

    useEffect(() => {
        const customerCommentService = new CustomerCommentService();
        customerCommentService
            .getCustomerCommentHome({})
            .then(response => setCustomerComments(response))
            .catch(err => console.error('Error fetching customer comments:', err));
    }, []);

    const swiperConfig = {
        spaceBetween: 20,
        centeredSlides: true,
        centeredSlidesBounds: true,
        loop: true,
        loopAdditionalSlides: 2,
        watchOverflow: true,
        navigation: {
            nextEl: `.${styles['nav']} .bw_hover_prev`,
            prevEl: `.${styles['nav']} .bw_hover_next`,
        },
        breakpoints: {
            0: {slidesPerView: 1.3, spaceBetween: 12},
            1024: {slidesPerView: 3},
        },
        onInit: swiper => {
            // record effective slidesPerView (could be number or 'auto')
            const spv = swiper.params.slidesPerView;
            setCurrentSlidesPerView(spv);
        },
        onResize: swiper => {
            setCurrentSlidesPerView(swiper.params.slidesPerView);
        },
    };

    // Re-center to second logical slide AFTER data arrives & Swiper mounted
    useEffect(() => {
        if (!swiperRef.current) return;
        const swiper = swiperRef.current;
        if (customerComments.length > 1) {
            swiper.update(); // ensure sizes recalculated
            if (swiper.params.loop) {
                swiper.slideToLoop(1, 0, false); // second logical slide, no animation
            } else {
                swiper.slideTo(1, 0, false);
            }
        }
    }, [customerComments]);

    const openMedia = cc => {
        if (!cc.media_url) return;
        setCustomerComment({
            media_url: cc.media_url,
            media_type: cc.is_video === 1 ? 'video' : 'image',
        });
        setIsMediaPopupOpen(true);
    };

    const handleSlideClick = (index, cc) => {
        if (swiperRef.current) {
            // Center the clicked slide (loop-aware)
            swiperRef.current.slideToLoop(index, 400);
        }
        // Optional: still open media if it has one
        if (cc.media_url) openMedia(cc);
    };

    return (
        <>
            <section className={`${styles['customer-comments']} container`}>
                <header>
                    <h2 className={styles['title']}>
                        <span>{getLocaleContent(locale, 'page.customer.impression')}</span>{' '}
                        {getLocaleContent(locale, 'page.customer.patient')} <span>TNH</span>
                    </h2>
                </header>
                <div className={styles['swiper-container']}>
                    <Swiper {...swiperConfig} onSwiper={sw => (swiperRef.current = sw)} className={styles['swiper']}>
                        {customerComments.map((cc, index) => (
                            <SwiperSlide key={index}>
                                {({isActive}) => (
                                    <div
                                        className={`${styles['swiper-slide']} ${
                                            isActive ? styles['swiper-slide-active'] : ''
                                        }`}
                                        onClick={() => handleSlideClick(index, cc)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={e =>
                                            (e.key === 'Enter' || e.key === ' ') && handleSlideClick(index, cc)
                                        }
                                        style={{cursor: 'pointer'}}>
                                        {cc.media_url ? (
                                            cc.is_video === 1 ? (
                                                <div
                                                    className={styles['video-wrapper']}
                                                    style={{position: 'relative'}}
                                                    onMouseEnter={() => setHoveredIndex(index)}
                                                    onMouseLeave={() => setHoveredIndex(null)}>
                                                    <video
                                                        ref={el => (videoRefs.current[index] = el)}
                                                        src={cc.media_url}
                                                        playsInline
                                                        style={{width: '100%', display: 'block'}}
                                                        onPlay={() => setPlayingIndex(index)}
                                                        onPause={() => setPlayingIndex(null)}
                                                    />
                                                    {playingIndex !== index && (
                                                        <button
                                                            className={styles['play-btn']}
                                                            type="button"
                                                            aria-label="Play"
                                                            // onClick={e => {
                                                            //     e.stopPropagation();
                                                            //     const v = videoRefs.current[index];
                                                            //     v && v.play();
                                                            // }}
                                                        >
                                                            <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                                                                <circle cx="30" cy="30" r="30" fill="#006940" />
                                                                <polygon points="24,18 44,30 24,42" fill="#fff" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {playingIndex === index && hoveredIndex === index && (
                                                        <button
                                                            className={styles['play-btn']}
                                                            // onClick={e => {
                                                            //     e.stopPropagation();
                                                            //     const v = videoRefs.current[index];
                                                            //     v && v.pause();
                                                            // }}
                                                            type="button"
                                                            aria-label="Pause">
                                                            <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                                                                <circle cx="30" cy="30" r="30" fill="#006940" />
                                                                <rect x="22" y="18" width="5" height="24" fill="#fff" />
                                                                <rect x="33" y="18" width="5" height="24" fill="#fff" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <img src={cc.media_url} alt="image" />
                                            )
                                        ) : (
                                            <img src="/bw_image/dv-kham-suc-khoe.png" alt="default" />
                                        )}
                                    </div>
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
                <div className={styles['nav']}>
                    <button className="navbtn bw_hover_next" aria-label="Prev"></button>
                    <button className="navbtn bw_hover_prev" aria-label="Next"></button>
                </div>
                {/* <div className={styles['see__more']}>
                    <button
                        className={styles['see__more-btn']}
                        onClick={() => (window.location.href = '/cam-nhan-khach-hang')}>
                        {getLocaleContent(locale, 'common.see-all')}
                    </button>
                </div> */}
            </section>
            <MediaPopup
                isOpen={isMediaPopupOpen}
                onClose={() => setIsMediaPopupOpen(false)}
                type={customerComment.media_type}
                src={customerComment.media_url}
            />
        </>
    );
}
